import { useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import I18n from 'i18n-js';

import { claimOffer } from '@lootboy/lib/slices/deals/thunks';
import { TOffer } from '@lootboy/lib/types/deals';

import Alert from '~/components/Alert';
import { Expandable } from '~/components/atoms/Expandable';
import ButtonClean from '~/components/ButtonClean';
import Image from '~/components/Image';

import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { LOGIN_REQUIRED_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useDealContext } from '~/views/DealsView/DealsContext';
import {
  OfferTicket,
  TOfferTicketRef,
} from '~/views/DealsView/OfferGroup/OfferTile/OfferTicket';

import { ReactComponent as CheckIcon } from '~/assets/images/iconsLibrary/check-circle.svg';
import { ReactComponent as Spinner } from '~/assets/images/iconsLibrary/loading-spinner.svg';

import constants from '~/sass/consts.module.scss';

import styles from './OfferTile.module.scss';

type TOfferTileProps = {
  offer: TOffer;
  code: string | undefined;
  isSingleOffer?: boolean;
};

export function OfferTile({
  offer,
  code,
  isSingleOffer = false,
}: TOfferTileProps): JSX.Element {
  const alert = useAlert();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isLoggedIn = useLoggedIn();
  const [isLoading, setLoading] = useState(false);
  const { setExpandedOffer, expandedOffer } = useDealContext();
  const isExpanded = expandedOffer === offer._id;

  const ticketRef = useRef<TOfferTicketRef>(null);

  const onOfferClick = async () => {
    if (!isLoggedIn) {
      history.push(LOGIN_REQUIRED_PATH);
      return;
    }
    if (isExpanded) {
      setExpandedOffer(null);
      return;
    }
    if (code) {
      setExpandedOffer(offer._id);
      return;
    }
    try {
      setLoading(true);
      await dispatch(claimOffer(offer._id)).unwrap();
      setExpandedOffer(offer._id);
    } catch (error) {
      const { message } = parseLootBoyError(error);
      alert.error(
        <Alert
          title={I18n.t('common_error')}
          body={message}
        />,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <ButtonClean
        onClick={onOfferClick}
        className={styles.expandButton}
      >
        <Image
          alt=""
          path={offer.image}
          title={offer.title}
          className={styles.image}
        />
        {!!code && <CheckIcon className={styles.checkIcon} />}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <Spinner className={styles.spinner} />
          </div>
        )}
      </ButtonClean>
      <Expandable
        isExpanded={!!code && isExpanded}
        onExpanded={() => {
          if (ticketRef.current) {
            ticketRef.current.scroll();
          }
        }}
      >
        <OfferTicket
          ref={ticketRef}
          code={code ?? ''}
          link={offer.buttonLink}
          linkLabel={offer.buttonTitle}
          isLinkInline={
            isSingleOffer &&
            window.innerWidth > Number(constants.smallMobileView)
          }
          isDisplayed={isExpanded}
        />
      </Expandable>
    </div>
  );
}
